'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Navbar from '../../../../components/Navbar';
import HorizontalFilterBar from '../../../../components/HorizontalFilterBar';
import FilterPanel from '../../../../components/FilterPanel';
import { api } from '@/lib/apiClient';
import { useSearchParams, useRouter } from 'next/navigation';
import AdBanner from '../../../../components/AdBanner';
import ExpertHelpCard from '../../../../components/ExpertHelpCard';
import useCheckLogin from '../../../../components/CheckLogin';
import MobileListingTopBar from '../../../../components/MobileListingTopBar';
import Footer from '../../../../components/Footer';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/proxy/';

const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;

    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) {
        return 'Posted today';
    } else if (diffDays <= 30) {
        return `Posted ${diffDays} days ago`;
    } else {
        return `Posted on ${date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
};

export const formatSinglePrice = (val) => {
    if (val === null || val === undefined || val === '') return '';
    if (typeof val === 'string') {
        val = val.replace(/₹|,/g, '').trim();
    }
    const num = parseFloat(val);
    if (isNaN(num) || num <= 0) return '';
    if (num >= 10000000) {
        const cr = num / 10000000;
        return `₹${parseFloat(cr.toFixed(2))} Cr`;
    }
    if (num >= 100000) {
        const lac = num / 100000;
        return `₹${parseFloat(lac.toFixed(2))} Lac`;
    }
    if (num >= 100) {
        const cr = num / 100;
        return `₹${parseFloat(cr.toFixed(2))} Cr`;
    }
    return `₹${parseFloat(num.toFixed(2))} Lac`;
};

export const formatProjectPrice = (priceStr, minPrice, maxPrice) => {
    if (minPrice != null && minPrice !== '' && !isNaN(parseFloat(minPrice))) {
        const minVal = parseFloat(minPrice);
        const maxVal = maxPrice != null && maxPrice !== '' && !isNaN(parseFloat(maxPrice)) ? parseFloat(maxPrice) : null;
        const minFormatted = formatSinglePrice(minVal);
        if (maxVal && maxVal !== minVal) {
            const maxFormatted = formatSinglePrice(maxVal);
            if (minFormatted && maxFormatted) return `${minFormatted} - ${maxFormatted}`;
        }
        if (minFormatted) return minFormatted;
    }

    if (!priceStr || typeof priceStr !== 'string') return priceStr || '';

    if (/(?:lac|cr|crore|l\b)/i.test(priceStr)) {
        return priceStr;
    }

    const cleaned = priceStr.replace(/,/g, '');
    const rangeMatch = cleaned.match(/₹?\s*(\d+(?:\.\d+)?)\s*-\s*₹?\s*(\d+(?:\.\d+)?)/);
    if (rangeMatch) {
        const minVal = parseFloat(rangeMatch[1]);
        const maxVal = parseFloat(rangeMatch[2]);
        const minFormatted = formatSinglePrice(minVal);
        const maxFormatted = formatSinglePrice(maxVal);
        if (minFormatted && maxFormatted && minVal !== maxVal) {
            return `${minFormatted} - ${maxFormatted}`;
        }
        return minFormatted || maxFormatted || priceStr;
    }

    const singleMatch = cleaned.match(/₹?\s*(\d+(?:\.\d+)?)/);
    if (singleMatch) {
        const formatted = formatSinglePrice(parseFloat(singleMatch[1]));
        if (formatted) return formatted;
    }

    return priceStr;
};

export const getProjectPriceRangeInLacs = (proj) => {
    if (!proj) return { min: 0, max: 0 };
    let minLacs = null;
    let maxLacs = null;

    if (proj.min_price != null && !isNaN(parseFloat(proj.min_price))) {
        const num = parseFloat(proj.min_price);
        minLacs = num >= 100000 ? num / 100000 : num;
    }
    if (proj.max_price != null && !isNaN(parseFloat(proj.max_price))) {
        const num = parseFloat(proj.max_price);
        maxLacs = num >= 100000 ? num / 100000 : num;
    }

    if (minLacs !== null && maxLacs !== null) {
        return { min: minLacs, max: maxLacs };
    }

    const range = proj.priceRange || '';
    const cleaned = range.replace(/,/g, '');
    const rangeMatchUnits = cleaned.match(/₹?\s*([\d.]+)\s*(L|Cr|Lac|Crore)?\s*-\s*₹?\s*([\d.]+)\s*(L|Cr|Lac|Crore)?/i);
    if (rangeMatchUnits) {
        const val1 = parseFloat(rangeMatchUnits[1]);
        const unit1 = (rangeMatchUnits[2] || '').toLowerCase();
        const val2 = parseFloat(rangeMatchUnits[3]);
        const unit2 = (rangeMatchUnits[4] || '').toLowerCase();

        const toLacs = (val, unit) => {
            if (unit.startsWith('c')) return val * 100;
            if (unit.startsWith('l')) return val;
            if (val >= 100000) return val / 100000;
            return val;
        };

        const min = toLacs(val1, unit1 || unit2);
        const max = toLacs(val2, unit2 || unit1);
        return { min: minLacs !== null ? minLacs : min, max: maxLacs !== null ? maxLacs : max };
    }

    const singleMatch = cleaned.match(/₹?\s*([\d.]+)\s*(L|Cr|Lac|Crore)?/i);
    if (singleMatch) {
        const val = parseFloat(singleMatch[1]);
        const unit = (singleMatch[2] || '').toLowerCase();
        let lacs = val;
        if (unit.startsWith('c')) lacs = val * 100;
        else if (val >= 100000) lacs = val / 100000;
        return { min: minLacs !== null ? minLacs : lacs, max: maxLacs !== null ? maxLacs : lacs };
    }

    return { min: minLacs || 0, max: maxLacs || 0 };
};

// Mock projects data
const mockProjects = [
    {
        id: 'proj-001',
        name: 'DreamAcres Heights',
        developer: 'DreamAcres Builders',
        location: 'OMR, Chennai',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600',
        priceRange: '₹65L - ₹1.2Cr',
        configurations: ['2 BHK', '3 BHK', '4 BHK'],
        status: 'Under Construction',
        possession: 'Dec 2025',
        totalUnits: 450,
        availableUnits: 124,
        amenities: ['Swimming Pool', 'Gym', 'Club House', 'Garden'],
        isRera: true,
        isFeatured: true,
    },
    {
        id: 'proj-002',
        name: 'Urban Oasis',
        developer: 'Skyline Developers',
        location: 'Anna Nagar, Chennai',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600',
        priceRange: '₹85L - ₹1.8Cr',
        configurations: ['3 BHK', '4 BHK'],
        status: 'Ready to Move',
        possession: 'Ready',
        totalUnits: 280,
        availableUnits: 45,
        amenities: ['Swimming Pool', 'Gym', 'Tennis Court', 'Jogging Track'],
        isRera: true,
        isFeatured: true,
    },
    {
        id: 'proj-003',
        name: 'Green Valley Villas',
        developer: 'Nature Homes',
        location: 'ECR, Chennai',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600',
        priceRange: '₹1.5Cr - ₹3.5Cr',
        configurations: ['3 BHK Villa', '4 BHK Villa'],
        status: 'Under Construction',
        possession: 'Mar 2026',
        totalUnits: 85,
        availableUnits: 32,
        amenities: ['Private Garden', 'Club House', 'Security', 'Power Backup'],
        isRera: true,
        isFeatured: false,
    },
    {
        id: 'proj-004',
        name: 'Metro Heights',
        developer: 'City Builders',
        location: 'Velachery, Chennai',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1574362848149-11496d93a7c7?w=600',
        priceRange: '₹55L - ₹95L',
        configurations: ['2 BHK', '3 BHK'],
        status: 'Under Construction',
        possession: 'Jun 2025',
        totalUnits: 320,
        availableUnits: 89,
        amenities: ['Gym', 'Children Play Area', 'Parking', 'Security'],
        isRera: true,
        isFeatured: false,
    },
];



const property = {
    agent: {
        name: "John Doe",
        role: "Property Agent"
    },
    costs: {
        rent: 25000,
        deposit: 300000
    }
};

const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'newest', label: 'Newest First' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
];

function convertToArray(str) {
    if (!str) return [];
    return str.split('-').map(num => parseInt(num, 10));
}

function ProjectCardSkeleton() {
    return (
        <div className="relative p-2 md:p-3 bg-white border border-slate-100 rounded-2xl md:rounded-[20px] shadow-xs flex flex-row gap-3 md:gap-6 items-center animate-pulse">
            <div className="relative w-[110px] h-[82px] xs:w-[125px] xs:h-[94px] md:w-60 md:h-40 bg-slate-200 rounded-xl md:rounded-2xl shrink-0" />
            <div className="flex-1 min-w-0 flex flex-col h-full justify-between py-1">
                <div>
                    <div className="h-6 w-36 bg-slate-200 rounded-md"></div>
                    <div className="h-4 w-1/2 bg-slate-200 rounded-md mt-2"></div>
                    <div className="h-3 w-1/3 bg-slate-200 rounded-md mt-2"></div>
                </div>
                <div className="hidden md:grid bg-gray-50 rounded-lg p-3 mt-3 mb-3 grid-cols-2 gap-4">
                    <div className="h-6 bg-slate-200/60 rounded"></div>
                    <div className="h-6 bg-slate-200/60 rounded"></div>
                </div>
                <div className="flex md:hidden gap-3 mt-2">
                    <div className="h-3 w-16 bg-slate-200 rounded"></div>
                    <div className="h-3 w-12 bg-slate-200 rounded"></div>
                </div>
            </div>
        </div>
    );
}

export default function ProjectsListingPage() {
    const isLoggedIn = useCheckLogin();
    const [filters, setFilters] = useState({});
    const [sortBy, setSortBy] = useState('relevance');
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
    const [mockProjects, setMockProjects] = useState([]);
    const [visibleCount, setVisibleCount] = useState(3);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('grid');
    const searchParams = useSearchParams();
    const router = useRouter();
    const cityId = searchParams.get("cityId");
    const [city_name, setCityName] = useState("");
    const [localities, setLocalities] = useState("");
    const [parms, setParms] = useState("");
    const [showInterestedPopup, setShowInterestedPopup] = useState(false);
    const [isLoggedInContext, setIsLoggedInContext] = useState(false);
    const loadMoreTriggerRef = useRef(null);

    // Read localities from query params if any are present
    const localitiesParam = searchParams.get("localities");
    const initialLocalities = localitiesParam ? localitiesParam.split('-').map(num => parseInt(num, 10)) : [];

    const [localitiesIds, setLocalitiesIds] = useState(initialLocalities);

    useEffect(() => {
        fetchProperties();
    }, [searchParams]);

    const fetchProperties = async () => {
        try {
            setLoading(true);
            const localitiesArray = convertToArray(searchParams.get("localities"));
            setParms(searchParams.toString());
            setLocalitiesIds(localitiesArray);
            let whereClause = "city_id=$1";
            let params = [cityId];

            const res = await api.post('selectwithout/view_home_cities', { sqlData: "*", where: "id=$1", whereData: [cityId] });
            //console.log("res.data", res.data);
            let city_name = res?.data?.[0]?.name || '';
            setCityName(city_name);
            if (localitiesArray && localitiesArray.length > 0) {
                const res1 = await api.post('selectwithout/master_localities', {
                    sqlData: "locality_name as name",
                    where: "id = ANY($1)",
                    whereData: [localitiesArray],
                });
                if (res1.success === 1 && Array.isArray(res1.data)) {
                    let localitiesNames = "";
                    for (let i = 0; i < res1.data.length; i++) {
                        localitiesNames += res1.data[i].name + ", ";
                    }
                    setLocalities(localitiesNames);
                }
            } else {
                setLocalities("");
            }

            if (localitiesArray && localitiesArray.length > 0) {
                whereClause += " and locality_id = ANY($2)";
                params.push(localitiesArray);
            }

            whereClause += " order by created_at desc";

            const response = await api.post('selectwithout/view_home_projects',
                {
                    sqlData: "*",
                    where: whereClause,
                    whereData: params,
                });
            console.log("response.data", response.data);
            if (response?.data && Array.isArray(response.data)) {
                setMockProjects(response.data);
            } else {
                setMockProjects([]);
            }
        } catch (error) {
            console.error('Error fetching properties:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter and Sort logic
    const filteredProjects = mockProjects.filter(project => {
        // BHK (configurations array matching)
        if (filters.bhk?.length) {
            if (!project.configurations) return false;
            const configBedroomsList = project.configurations.map(c => {
                const match = c.match(/(\d+)\s*BHK/i);
                return match ? parseInt(match[1], 10) : null;
            }).filter(Boolean);

            const matchesBhk = filters.bhk.some(val => {
                if (val === '3+') {
                    return configBedroomsList.some(beds => beds >= 3);
                }
                const valInt = parseInt(val, 10);
                return configBedroomsList.includes(valInt);
            });
            if (!matchesBhk) return false;
        }

        // Budget
        if (filters.budgetMin !== undefined) {
            const { min: minPriceLacs, max: maxPriceLacs } = getProjectPriceRangeInLacs(project);
            const filterMin = filters.budgetMin;
            const filterMax = filters.budgetMax;

            const matchesBudget = maxPriceLacs >= filterMin && (filterMax === null || minPriceLacs <= filterMax);
            if (!matchesBudget) return false;
        }

        // Status
        if (filters.status?.length) {
            const s = project.status?.toLowerCase() || '';
            const matchesStatus = filters.status.some(opt => {
                if (opt === 'ready') return s === 'completed';
                if (opt === 'later') return s === 'ongoing' || s === 'upcoming';
                return false;
            });
            if (!matchesStatus) return false;
        }

        return true;
    });

    const getProjectPriceMin = (proj) => {
        const { min } = getProjectPriceRangeInLacs(proj);
        return min || 0;
    };

    if (sortBy === 'price_low') {
        filteredProjects.sort((a, b) => getProjectPriceMin(a) - getProjectPriceMin(b));
    } else if (sortBy === 'price_high') {
        filteredProjects.sort((a, b) => getProjectPriceMin(b) - getProjectPriceMin(a));
    } else if (sortBy === 'newest') {
        filteredProjects.sort((a, b) => {
            const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
            if (timeA && timeB) {
                return timeB - timeA;
            }
            const idA = parseInt(a.id, 10) || 0;
            const idB = parseInt(b.id, 10) || 0;
            return idB - idA;
        });
    }

    // Reset pagination when filters, projects or sort change
    useEffect(() => {
        setVisibleCount(3);
        setIsLoadingMore(false);
    }, [filters, mockProjects, sortBy]);

    const hasMore = visibleCount < filteredProjects.length;
    const visibleProjects = filteredProjects.slice(0, visibleCount);

    const handleLoadMore = useCallback(() => {
        if (isLoadingMore) return;
        setIsLoadingMore(true);
        setTimeout(() => {
            setVisibleCount(prev => prev + 3);
            setIsLoadingMore(false);
        }, 600);
    }, [isLoadingMore]);

    // IntersectionObserver to auto-load when bottom is reached
    useEffect(() => {
        if (!hasMore || loading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isLoadingMore) {
                    handleLoadMore();
                }
            },
            {
                root: null,
                rootMargin: '150px',
                threshold: 0.1,
            }
        );

        const currentTarget = loadMoreTriggerRef.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [hasMore, loading, isLoadingMore, handleLoadMore]);

    // Window scroll fallback for reliable bottom detection across all devices
    useEffect(() => {
        if (!hasMore || loading || isLoadingMore) return;

        const handleScroll = () => {
            const scrollHeight = document.documentElement.scrollHeight;
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const clientHeight = window.innerHeight;

            if (scrollTop + clientHeight >= scrollHeight - 300) {
                handleLoadMore();
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hasMore, loading, isLoadingMore, handleLoadMore]);

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        console.log('Project Filters changed:', newFilters);
    };

    return (
        <>
            <Navbar />
            <MobileListingTopBar
                filters={filters}
                onFilterChange={handleFilterChange}
                listingType="projects"
                onSearch={(city, locs) => {
                    const params = new URLSearchParams();
                    if (city?.id) params.set('cityId', city.id);
                    if (locs?.length > 0) params.set('localities', locs.map(l => l.id).join('-'));
                    const citySlug = city?.name?.toLowerCase() || 'all';
                    router.push(`/projects/${citySlug}?${params.toString()}`);
                }}
            />
            <main className="min-h-screen bg-gray-50 pb-20 lg:pb-0 mb-8">
                {/* Horizontal Filter Bar - Sticky */}
                <div className="hidden lg:block sticky top-16 z-40 horizontal-filter-bar">
                    <HorizontalFilterBar
                        filters={filters}
                        onChange={handleFilterChange}
                        listingType="projects"
                    />
                </div>

                {/* Page Header */}
                <div className="bg-gray">
                    <div className="container-custom py-4">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-5 mb-3">
                            {/* LEFT - Breadcrumb */}
                            <nav
                                className="flex flex-wrap items-center gap-2 text-sm text-gray-500"
                                aria-label="Breadcrumb"
                            >
                                <span className="text-gray-900 font-medium">
                                    {loading ? (
                                        <span className="text-gray-500">Searching projects in {city_name || 'selected city'}...</span>
                                    ) : (
                                        <>
                                            <strong>Available {filteredProjects.length} Projects</strong> in {city_name}
                                        </>
                                    )}
                                </span>
                            </nav>
                            {/* RIGHT – Relevance Select */}
                            <div className="flex items-center justify-between md:justify-end gap-2 w-full md:w-auto shrink-0">
                                <label htmlFor="sortBy" className="text-sm text-gray-600 whitespace-nowrap">
                                    Sort by:
                                </label>
                                <select
                                    id="sortBy"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="h-9 w-full md:w-auto text-sm px-2 rounded-md bg-white border border-gray-300 text-gray-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                >
                                    {sortOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Split Layout */}
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-0 items-start">

                        {/* Left: Project List (8 cols) */}
                        <div className="lg:col-span-8 flex flex-col gap-3">
                            {loading ? (
                                <div className="flex flex-col gap-3">
                                    {[1, 2, 3].map((n) => (
                                        <ProjectCardSkeleton key={n} />
                                    ))}
                                </div>
                            ) : filteredProjects.length === 0 ? (
                                <div className="bg-white rounded-2xl p-10 text-center border border-slate-100 shadow-xs flex flex-col items-center justify-center">
                                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-2xl mb-3">
                                        🏢
                                    </div>
                                    <h3 className="text-base font-bold text-slate-800 mb-1">
                                        No Projects Found
                                    </h3>
                                    <p className="text-xs text-slate-500 max-w-sm mb-4">
                                        We couldn't find any projects matching your selected criteria in {city_name || 'this city'}. Try adjusting your filters.
                                    </p>
                                    {Object.keys(filters).length > 0 && (
                                        <button
                                            onClick={() => setFilters({})}
                                            className="px-4 py-2 text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors cursor-pointer"
                                        >
                                            Reset All Filters
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {/* Projects List */}
                                    {visibleProjects.map((project) => (
                                        <Link
                                            key={project.id}
                                            href={`/projects/p/${project.id}?${parms}`}
                                            className="group relative p-2 md:p-3 bg-white border border-slate-100 rounded-2xl md:rounded-[20px] shadow-xs hover:shadow-md transition-all duration-300 flex flex-row gap-3 md:gap-6 items-center"
                                        >
                                            {/* Image Section */}
                                            <div className="relative w-[110px] h-[82px] xs:w-[125px] xs:h-[94px] md:w-60 md:h-40 md:shrink-0 overflow-hidden rounded-xl md:rounded-2xl shrink-0">
                                                <img
                                                    src={project.image ? (project.image.startsWith('http') ? project.image : `${BASE_URL}${project.image}`) : '/no-image.jpeg'}
                                                    alt={project.name}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />

                                                {/* Badges */}
                                                <div className="absolute top-2 left-2 flex flex-col gap-1">
                                                    {project.isRera && (
                                                        <span className="px-1.5 py-0.5 text-[9px] md:text-xs font-bold uppercase bg-green-500 text-white rounded shadow-sm w-fit">
                                                            RERA Reg
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Status Badge (Bottom Left on Image) */}
                                                <div className="absolute bottom-2 left-2">
                                                    <span className={`px-1.5 py-0.5 text-[9px] md:text-xs font-semibold rounded-full shadow-sm backdrop-blur-sm ${project.status === 'Ready to Move' ? 'bg-green-500/90 text-white' : 'bg-orange-500/90 text-white'
                                                        }`}>
                                                        {project.status}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Content Section */}
                                            <div className="flex-1 min-w-0 flex flex-col h-full justify-between py-0.5 md:py-1">

                                                {/* Price and developer info */}
                                                <div className="flex justify-between items-start">
                                                    <div className="min-w-0">
                                                        <div className="text-base sm:text-lg md:text-2xl font-bold text-primary truncate">
                                                            {formatProjectPrice(project.priceRange, project.min_price, project.max_price)} <span className="text-[10px] md:text-xs font-semibold text-slate-500">onwards</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Title & Developer */}
                                                <div className="mt-0.5 md:mt-1">
                                                    <h3 className="text-xs sm:text-sm md:text-lg font-bold text-slate-800 line-clamp-1 group-hover:text-primary transition-colors">
                                                        {project.name}
                                                    </h3>
                                                    <p className="text-[10px] sm:text-xs text-slate-500 font-medium line-clamp-1 mt-0.5">{project.developer}</p>
                                                </div>

                                                {/* Location & Posted Date */}
                                                <div className="flex items-center justify-between gap-2 mt-1">
                                                    <p className="flex items-center gap-1 text-[10px] sm:text-xs md:text-sm text-slate-500 truncate">
                                                        <svg className="w-3.5 h-3.5 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        </svg>
                                                        <span className="truncate">{project.location}</span>
                                                    </p>
                                                    {project.created_at && (
                                                        <span className="text-[10px] md:text-xs text-slate-400 shrink-0 bg-slate-50 px-2 py-0.5 rounded flex items-center gap-1">
                                                            <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                            {formatDate(project.created_at)}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Mobile Configs Row */}
                                                <div className="flex items-center gap-3 text-slate-500 text-[10px] sm:text-xs mt-2 md:hidden">
                                                    {project.configurations && (
                                                        <span className="flex items-center gap-1 font-medium">
                                                            <span className="text-slate-400"></span> {project.configurations.join(', ')}
                                                        </span>
                                                    )}
                                                    <span className="flex items-center gap-1 font-medium">
                                                        <strong>{project.availableUnits}</strong> left
                                                    </span>
                                                </div>

                                                {/* Desktop Configs & Possession */}
                                                <div className="hidden md:grid bg-gray-50 rounded-lg p-3 mt-3 mb-3 grid-cols-2 gap-y-2 gap-x-4">
                                                    <div>
                                                        <span className="block text-xs text-gray-500 uppercase font-semibold">Configurations</span>
                                                        <span className="text-sm font-medium text-gray-800">{project.configurations?.join(', ')}</span>
                                                    </div>
                                                    <div>
                                                        <span className="block text-xs text-gray-500 uppercase font-semibold">Possession</span>
                                                        <span className="text-sm font-medium text-gray-800">{project.possession}</span>
                                                    </div>
                                                </div>

                                                {/* Desktop Amenities Preview */}
                                                <div className="hidden md:flex gap-2 mb-3 overflow-hidden">
                                                    {project.amenities?.slice(0, 3).map((amenity, idx) => (
                                                        <span key={idx} className="px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-600 whitespace-nowrap">
                                                            {amenity}
                                                        </span>
                                                    ))}
                                                    {project.amenities?.length > 3 && (
                                                        <span className="px-2 py-1 text-xs text-gray-400">+{project.amenities.length - 3} more</span>
                                                    )}
                                                </div>

                                                {/* Desktop CTA */}
                                                <div className="hidden md:flex mt-auto pt-4 border-t border-gray-100 items-center justify-between">
                                                    <div className="text-sm text-gray-500">
                                                        <strong>{project.availableUnits}</strong> units left
                                                    </div>
                                                    <span className="text-sm font-bold text-primary group-hover:underline flex items-center gap-1">
                                                        View Details
                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                                        </svg>
                                                    </span>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}

                                    {/* Bottom Sentinel & Loading indicator on scroll */}
                                    {hasMore && (
                                        <div ref={loadMoreTriggerRef} className="py-4 flex flex-col items-center justify-center gap-3">
                                            {isLoadingMore ? (
                                                <>
                                                    <div className="w-full flex flex-col gap-3">
                                                        <ProjectCardSkeleton />
                                                    </div>
                                                    <div className="flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white border border-slate-200/80 shadow-xs text-slate-600 text-xs font-medium">
                                                        <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>
                                                        <span>Loading more projects...</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="h-6 w-full flex items-center justify-center">
                                                    <span className="text-[11px] text-slate-400">Scroll down to load more</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {!hasMore && filteredProjects.length > 3 && (
                                        <div className="py-6 text-center">
                                            <p className="text-xs font-medium text-slate-400 flex items-center justify-center gap-1.5">
                                                <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                You've viewed all {filteredProjects.length} projects
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Right: Ad Sidebar (4 cols) */}
                        <aside className="lg:col-span-4 hidden lg:flex flex-col gap-6 sticky top-24">
                            {/* Featured Widget */}
                            <ExpertHelpCard
                                title="Plot Buying Guide"
                                description="Confused about DTCP vs CMDA? Download our free guide on how to safely buy land in Chennai."
                                buttonText="Post Property"
                                link={isLoggedIn ? "/seller/post-property" : "/register"}
                                bgClass="bg-gradient-to-br from-emerald-50 to-teal-50/50"
                                borderClass="border-slate-100"
                                titleColorClass="text-emerald-900"
                                descColorClass="text-emerald-700"
                                btnTextColorClass="text-emerald-600 group-hover:text-emerald-800"
                                glowColor1="#10b981"
                                glowColor2="#2dd4bf"
                            />
                            {/* Ad Banners */}
                            <AdBanner
                                type="projects"
                                noofads={2}
                            />
                        </aside>

                    </div>
                </div>

                {/* Mobile Filter Modal */}
                {isMobileFilterOpen && (
                    <div className="fixed inset-0 z-50 lg:hidden">
                        <div
                            className="absolute inset-0 bg-black/50"
                            onClick={() => setIsMobileFilterOpen(false)}
                        />
                        <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white overflow-y-auto">
                            <FilterPanel
                                filters={filters}
                                onChange={handleFilterChange}
                                listingType="projects" // Pass project type
                                isMobile={true}
                                onClose={() => setIsMobileFilterOpen(false)}
                            />
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </>
    );
}
